import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TOKEN_KEY = "driftex_admin_token";

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 15000,
});

// Uploaded images are stored as relative paths (/api/uploads/...) — resolve against backend URL
export const resolveImageUrl = (url) =>
  url && url.startsWith("http") ? url : `${BACKEND_URL}${url || ""}`;

/* ---------------- Admin session token ---------------- */

export const getAdminToken = () => sessionStorage.getItem(ADMIN_TOKEN_KEY);
export const setAdminToken = (token) => sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
export const clearAdminToken = () => sessionStorage.removeItem(ADMIN_TOKEN_KEY);

// attach X-Admin-Token to all admin API calls
api.interceptors.request.use((config) => {
  const url = config.url || "";
  if (url.startsWith("/admin") && url !== "/admin/auth") {
    const token = getAdminToken();
    if (token) config.headers["X-Admin-Token"] = token;
  }
  return config;
});

// on 401 from an admin endpoint the session expired — drop token and notify app
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const url = error?.config?.url || "";
    if (error?.response?.status === 401 && url.startsWith("/admin") && url !== "/admin/auth") {
      clearAdminToken();
      window.dispatchEvent(new Event("driftex-admin-unauthorized"));
    }
    return Promise.reject(error);
  }
);

export const adminAuth = (pin) => api.post("/admin/auth", { pin }).then((r) => r.data);

/* ---------------- Public content ---------------- */

export const getProducts = () => api.get("/products").then((r) => r.data);
export const getRaces = (season) =>
  api.get("/races", { params: season ? { season } : {} }).then((r) => r.data);
export const getRaceSeasons = () => api.get("/races/seasons").then((r) => r.data);
export const getAchievements = () => api.get("/achievements").then((r) => r.data);
export const getWorkshopServices = () =>
  api.get("/workshop-services").then((r) => r.data);
export const getGallery = () => api.get("/gallery").then((r) => r.data);
export const getYoutubeVideos = () => api.get("/youtube/videos").then((r) => r.data);

/* ---------------- Form submissions ---------------- */

export const postWorkshopInquiry = (payload) =>
  api.post("/workshop-inquiries", payload).then((r) => r.data);
export const postPartnershipInquiry = (payload) =>
  api.post("/partnership-inquiries", payload).then((r) => r.data);
export const postContactMessage = (payload) =>
  api.post("/contact-messages", payload).then((r) => r.data);
export const postOrder = (payload) => api.post("/orders", payload).then((r) => r.data);

/* ---------------- Admin: overview + lists ---------------- */

export const getAdminOverview = () => api.get("/admin/overview").then((r) => r.data);
export const getAdminOrders = () => api.get("/admin/orders").then((r) => r.data);
export const getAdminWorkshopInquiries = () =>
  api.get("/admin/workshop-inquiries").then((r) => r.data);
export const getAdminPartnershipInquiries = () =>
  api.get("/admin/partnership-inquiries").then((r) => r.data);
export const getAdminContactMessages = () =>
  api.get("/admin/contact-messages").then((r) => r.data);

/* ---------------- Admin: gallery ---------------- */

export const uploadGalleryPhoto = (formData) =>
  api
    .post("/admin/gallery", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    })
    .then((r) => r.data);
export const updateGalleryPhoto = (photoId, payload) =>
  api.put(`/admin/gallery/${photoId}`, payload).then((r) => r.data);
export const deleteGalleryPhoto = (photoId) =>
  api.delete(`/admin/gallery/${photoId}`).then((r) => r.data);

/* ---------------- Admin: races CRUD ---------------- */

export const getAdminRaces = () => api.get("/admin/races").then((r) => r.data);
export const createRace = (payload) =>
  api.post("/admin/races", payload).then((r) => r.data);
export const updateRace = (raceId, payload) =>
  api.put(`/admin/races/${raceId}`, payload).then((r) => r.data);
export const deleteRace = (raceId) =>
  api.delete(`/admin/races/${raceId}`).then((r) => r.data);

/* ---------------- Admin: products CRUD ---------------- */

export const getAdminProducts = () => api.get("/admin/products").then((r) => r.data);
export const createProduct = (formData) =>
  api
    .post("/admin/products", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    })
    .then((r) => r.data);
export const updateProduct = (productId, formData) =>
  api
    .put(`/admin/products/${productId}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    })
    .then((r) => r.data);
export const setProductVisibility = (productId, visible) =>
  api.put(`/admin/products/${productId}/visibility`, { visible }).then((r) => r.data);
export const deleteProduct = (productId) =>
  api.delete(`/admin/products/${productId}`).then((r) => r.data);
